import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { DurationFormat } from '@freelancer/pipes';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { PictureObjectFit } from '@freelancer/ui/picture';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { BackgroundColor, ExperienceType } from './experience.types';

@Component({
  selector: 'fl-experience',
  template: `
    <div class="Container">
      <div
        *ngIf="!imgSrc"
        class="GenericIconContainer"
        [attr.data-background-color]="backgroundColor"
        [flMarginRight]="'small'"
      >
        <fl-icon
          [fillIconFont]="false"
          [name]="placeholderIconName"
          [sizeTablet]="IconSize.MID"
          [size]="IconSize.SMALL"
          [useIconFont]="true"
          [color]="
            backgroundColor !== BackgroundColor.DEFAULT
              ? IconColor.LIGHT
              : IconColor.DARK
          "
        ></fl-icon>
      </div>
      <fl-picture
        *ngIf="imgSrc"
        i18n-alt="Card image alt text"
        class="ExperienceImg"
        [src]="imgSrc"
        [aspectRatioValue]="'square'"
        [objectFit]="PictureObjectFit.COVER"
        [alignCenter]="true"
        [borderRadiusBottom]="true"
        [borderRadiusTop]="true"
        [flMarginRight]="'small'"
      ></fl-picture>
      <div class="DetailsContainer">
        <div
          class="HeaderContainer"
          [flMarginBottom]="'xxxsmall'"
          [flMarginBottomTablet]="'xxsmall'"
        >
          <fl-text
            [flMarginRight]="'xxsmall'"
            [size]="size"
            [sizeDesktop]="sizeDesktop"
            [sizeDesktopXXLarge]="sizeDesktopXXLarge"
            [weight]="FontWeight.MEDIUM"
          >
            {{ mainTitle }}
          </fl-text>
          <div class="EditIconPositioningContainer">
            <div class="EditIconContainer">
              <fl-icon
                *ngIf="canEdit"
                class="Icon"
                i18n-label="Edit icon label"
                label="Edit"
                [name]="'ui-edit-outline'"
                [color]="IconColor.FOREGROUND"
                [hoverAnimation]="'highlight'"
                [size]="IconSize.MID"
                [clickable]="true"
                (click)="triggerEdit()"
              ></fl-icon>
            </div>
          </div>
        </div>
        <div
          [flShowMobile]="true"
          [flMarginBottom]="'xxsmall'"
        >
          <ng-container [ngTemplateOutlet]="dateTemplate"></ng-container>
        </div>
        <div [flMarginBottom]="'xxsmall'">
          <ng-content></ng-content>
        </div>
      </div>
      <div [flHideMobile]="true">
        <ng-container [ngTemplateOutlet]="dateTemplate"></ng-container>
      </div>
    </div>
    <ng-template #dateTemplate>
      <div class="DateExperienceContainer">
        <div
          class="DateExperienceContainer-wrapper"
          [flMarginBottom]="'xxxsmall'"
          [flMarginRight]="'xxsmall'"
          [flMarginRightTablet]="'none'"
        >
          <fl-text
            *ngIf="startDate"
            [size]="size"
            [sizeDesktop]="sizeDesktop"
            [sizeDesktopXXLarge]="sizeDesktopXXLarge"
            [weightTablet]="FontWeight.MEDIUM"
          >
            <ng-container
              *ngIf="
                experienceType === ExperienceType.EXPERIENCE;
                else shortFormat
              "
            >
              {{ startDate | date: 'MMM, yyyy' }}
              <ng-container
                *ngIf="endDate || endDate !== undefined; else presentLong"
              >
                - {{ endDate | date: 'MMM, yyyy' }}
              </ng-container>
              <ng-template #presentLong>
                <ng-container i18n="Present"> - Present </ng-container>
              </ng-template>
            </ng-container>

            <ng-template #shortFormat>
              {{ startDate | date: 'yyyy' }}
              <ng-container
                *ngIf="endDate || endDate !== undefined; else present"
              >
                - {{ endDate | date: 'yyyy' }}
              </ng-container>
              <ng-template #present>
                <ng-container i18n="Present"> - Present </ng-container>
              </ng-template>
            </ng-template>
          </fl-text>
          <fl-text
            *ngIf="singleDate"
            [size]="size"
            [sizeDesktop]="sizeDesktop"
            [sizeDesktopXXLarge]="sizeDesktopXXLarge"
            [weightTablet]="FontWeight.MEDIUM"
          >
            {{ singleDate | date: 'yyyy' }}
          </fl-text>
        </div>
        <fl-text
          *ngIf="duration"
          [size]="size"
          [sizeDesktop]="sizeDesktop"
          [sizeDesktopXXLarge]="sizeDesktopXXLarge"
          [flShowMobile]="true"
          [flMarginRight]="'xxsmall'"
        >
          {{ '•' }}
        </fl-text>

        <fl-text
          *ngIf="duration"
          [flMarginBottom]="'xxxsmall'"
          [size]="size"
          [sizeDesktop]="sizeDesktop"
          [sizeDesktopXXLarge]="sizeDesktopXXLarge"
        >
          {{ duration | duration: DurationFormat.LONG : startDate | flAsync }}
        </fl-text>
      </div>
    </ng-template>
  `,
  styleUrls: ['./experience.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExperienceComponent implements OnInit {
  BackgroundColor = BackgroundColor;
  ExperienceType = ExperienceType;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  DurationFormat = DurationFormat;
  PictureObjectFit = PictureObjectFit;
  TextSize = TextSize;

  placeholderIconName: string;

  @Input() backgroundColor = BackgroundColor.DEFAULT;
  @Input() experienceType = ExperienceType.EXPERIENCE;
  @Input() imgSrc?: string;
  @Input() mainTitle?: string;
  @Input() size = TextSize.SMALL;
  @Input() sizeDesktop?: TextSize;
  @Input() sizeDesktopXXLarge?: TextSize;

  /*
    By using startDate the UI will always display 2 text fields
    startDate & endDate or startDate & "Present" in case endDate is empty,
    as is required in Experience and Education
  */
  @Input() startDate?: number;
  @Input() endDate?: number;
  @Input() duration?: number;
  @Input() durationUntilPresent?: number = 0;

  /*
    By using singleDate the UI will always just 1 text field with a short format,
    as is used in Publications and Qualifications
  */
  @Input() singleDate?: number;

  /*
    Shows an edit icon next to the experience's title if true.
  */
  @Input() canEdit = false;

  @Output() clickEdit = new EventEmitter<void>();

  ngOnInit(): void {
    if (this.endDate === undefined && this.startDate !== undefined) {
      this.duration = Date.now() - this.startDate;
    }
    switch (this.experienceType) {
      case ExperienceType.EDUCATION:
        this.placeholderIconName = 'ui-education';
        break;
      case ExperienceType.PUBLICATION:
        this.placeholderIconName = 'ui-publication';
        break;
      case ExperienceType.QUALIFICATION:
        this.placeholderIconName = 'ui-bought-medal';
        break;
      case ExperienceType.EXPERIENCE:
      default:
        this.placeholderIconName = 'ui-work';
        break;
    }
  }

  triggerEdit(): void {
    this.clickEdit.emit();
  }
}
